<template>
  <Card title="Queries" no-body class="fill-height query-list-card">
    <template #header-suffix>
      <IconTooltip
        tooltip="You can run multiple queries for a single migration"
        tooltip-position="left"
      />
    </template>
    <div class="table-wrapper">
      <b-table-lite
        class="reads-table"
        hover
        small
        :items="reads"
        :fields="fields"
        tbody-tr-class="clickable"
        @row-clicked="(_, index) => selectQuery(index)"
      >
        <template v-slot:cell(query)="{ item }">
          <pre class="pre-wrap mb-0">{{ formatRead(item) }}</pre>
        </template>
        <template v-slot:cell(run)="{ item, index }">
          <b-form-checkbox
            switch
            size="lg"
            class="query-list-run-switch clickable"
            v-model="item.run"
            name="run"
            v-b-tooltip
            title="If on will run in migration"
            @change="v => updateRunQuery(index, v)"
          />
        </template>
        <template v-slot:cell(delete)="{ index }">
          <b-button
            pill
            :disabled="reads.length < 2"
            variant="light"
            @click.stop="deleteQuery(index)"
          >
            <i class="bi bi-trash"  />
          </b-button>
        </template>
      </b-table-lite>
    </div>

    <template v-slot:footer>
      <Button
        @click="addQuery"
        variant="success"
        :enabled="!hasEmptyRead"
        tooltip-position="left"
        :tooltip="emptyQuerText"
      >
        Add
      </Button>
    </template>
  </Card>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import omit from 'lodash/omit';

import Button from '@/components/Button.vue';
import Card from '@/components/Card.vue';
import IconTooltip from '@/components/IconTooltip.vue';
import store from '@/plugins/vuex';

export default defineComponent({
  name: 'QueryList',
  components: {
    Button,
    Card,
    IconTooltip,
  },
  props: {
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      fields: ['query', 'run', 'delete'],
    };
  },
  computed: {
    ...mapState({
      reads: (state: any) =>
        state.read.read?.map((read, i) =>
          state.read.currentIndex === i
            ? { ...read, _rowVariant: 'info' }
            : read
        ),
    }),
    hasEmptyRead(): boolean {
      return this.reads.some(r => !r.query?.trim());
    },
    emptyQuerText(): string | undefined {
      if (this.hasEmptyRead) {
        return 'You can\'t add queries while one is empty';
      }
      return undefined;
    },
  },
  methods: {
    updateRunQuery(index, run) {
      store.commit.read.updateRead({ index, read: { run } });
    },
    selectQuery(index) {
      store.dispatch.read.setCurrentReadIndex(index);
    },
    async deleteQuery(index) {
      let result = true;
      if (!store.getters.read.isQueryEmpty(index)) {
        // If query is empty, no need to confirm
        result = await confirm('Are you sure you want to delete the query?');
      }
      if (result) {
        store.commit.read.deleteRead(index);
      }
    },
    addQuery() {
      store.dispatch.read.newRead(null);
    },
    formatRead(read) {
      switch (store.state.source.type) {
        case 'mongodb':
          return JSON.stringify(omit(read, ['run', '_rowVariant']));
        default:
          return read?.query || '';
      }
    },
  },
});
</script>
<style scoped>
.query-list-card {
  display: flex;
}

.query-list-card .table-wrapper {
  flex: 1;
}
.query-list-run-switch {
  padding-left: 3.5rem !important;
  margin-bottom: 0.5rem;
}
</style>
<style>
.reads-table th:not(:first-child) {
  text-align: center;
  white-space: nowrap;
  width: 80px;
}

.reads-table tbody td {
  vertical-align: middle;
}

.reads-table td:not(:first-child) {
  text-align: center;
}
</style>
